/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute } from "@tanstack/react-router";
import { useAuth, useVenueDetailsBySlug, useI18N } from "common/hooks";
import { useEffect, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Helmet } from "react-helmet-async";
import { CanonicalTag } from "@/components/common/CanonicalTag";
import { Storage } from "common/storage";
import AccessibilityGuideContent from "../components/VenueDetailComplex/AccessibilityGuideContent";
import { getVenueSlugs, getStoredPasswordToken, getStoredSsoToken } from "@/lib/utils";

export type AccessibilityGuideRouteParams = {
  venueId: string; // from the route path"/accessibility-guide/$venueId"
};

export type AccessibilityGuideRouteSearch = {
  source?: "map" | "activity" | "notifications-page";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
  tab?: "accessibility" | "photos" | "alerts" | "reviews";
  alertsTab?: "mapper" | "public";
};

export const Route = createFileRoute("/accessibility-guide/$venueId")({
  component: AccessibilityGuide,
  validateSearch: (
    search: Record<string, unknown>,
  ): AccessibilityGuideRouteSearch => {
    return {
      source: search.source as "map" | "activity" | "notifications-page",
      showAuthModal: search.showAuthModal as boolean,
      cid: search.cid as string,
      tid: search.tid as string,
      tab: search.tab as "accessibility" | "photos" | "alerts" | "reviews",
      alertsTab: search.alertsTab as "mapper" | "public",
    };
  },
  parseParams: (params: Record<string, string>) => {
    return {
      venueId: params.venueId,
    };
  },
});

function AccessibilityGuide() {
  const { venueId: venueSlug } = Route.useParams();
  const { showAuthModal, cid, tid, tab, alertsTab } = Route.useSearch();
  const i18n = useI18N();

  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
  } = useVenueDetailsBySlug({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    type: "Complex",
    passwordToken: getStoredPasswordToken(venueSlug),
    ssoToken: getStoredSsoToken(venueSlug),
  });

  const { showModal } = useAuthModals();

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  return (
    <main id="main" className="bg-bg-brand-secondary-default">
      <Helmet>
        <title>
          {(() => {
            const currentCohort = data?.details?.cohortsData?.find(
              (cohort) => cohort.slug === venueSlug,
            );
            const venueName = data?.details?.name
              ? data.details.name.length > 30
                ? data.details.name.substring(0, 30) + "..."
                : data.details.name
              : "";

            if (currentCohort) {
              return `${currentCohort.cohort_name} | ${venueName} | ${i18n.get("appName")}`;
            }
            return venueName
              ? `${venueName} ${i18n.get("accessibilityGuideTitleSuffix")}`
              : i18n.get("accessibilityGuideTitleSuffix");
          })()}
        </title>
        {data?.details?.name && (
          <meta
            name="description"
            content={`${i18n.get("venueMetaDescriptionPrefix")} ${data.details.name} ${i18n.get("venueMetaDescriptionSuffix")}`}
          />
        )}
      </Helmet>
      <CanonicalTag />
      {(isAuthLoading || isDetailsLoading) && <Loader />}
      <section className="mb-2">
        {error && (
          <div className="max-w-[1160px] px-4 mx-auto overflow-hidden">
            <div>{error.message}</div>
          </div>
        )}

        {data && (
          <AccessibilityGuideContent
            venueDetailsResponse={data}
            venueSlug={venueSlug}
            onClose={() => {
              const { venueDetailsSlug } = getVenueSlugs(data.details);
              window.location.href = `/venuedetails/${venueDetailsSlug}`;
            }}
            onRefresh={() => {
              setTimeout(() => loadVenueDetails(), 1000);
            }}
            cid={cid}
            tid={tid}
            initialSpaceId={undefined}
            initialCategoryName={undefined}
            mode="accessibility-guide"
            initialTab={tab}
            initialAlertsTab={alertsTab}
          />
        )}
      </section>
    </main>
  );
}
